import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Droplets, AlertCircle, Download, RefreshCw, Mail, CheckCircle, Database, Loader } from 'lucide-react';
import businessService from '../services/businessService';

const SubscriptionCancelled = () => {
  const [exportRequested, setExportRequested] = useState(false);
  const [exportEmail, setExportEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleExportRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await businessService.exportData({
        includeCustomers: true,
        includeJobs: true,
        includeInvoices: true,
        includeLeads: true,
        includeServiceTypes: true
      });
      setExportRequested(true);
    } catch (err) {
      console.error('Error requesting data export:', err);
      const errorMessage = err.response?.data?.error || 'Failed to request data export. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-8">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center w-20 h-20 bg-gray-600 rounded-xl mx-auto mb-4">
            <Droplets className="text-white" size={40} />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Subscription Cancelled</h1>
          <p className="text-gray-600 text-lg">
            Your SuddyFlow subscription has been cancelled
          </p>
        </div>

        {/* Important Information */}
        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-blue-900 mb-1">
                Your data is safe for 30 days
              </h3>
              <p className="text-sm text-blue-800">
                We'll keep your business data for 30 days in case you change your mind. 
                After that, all data will be permanently deleted.
              </p>
            </div>
          </div>
        </div>

        {/* Data Export Section */}
        <div className="border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Database className="h-6 w-6 text-gray-600" />
            <h2 className="text-xl font-semibold text-gray-900">Export Your Data</h2>
          </div>
          
          {!exportRequested ? (
            <>
              <p className="text-gray-600 mb-4">
                Want to keep a copy of your business data? Request an export and we'll email you 
                a complete backup of your customers, jobs, invoices, and more.
              </p>

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <form onSubmit={handleExportRequest} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    required
                    value={exportEmail}
                    onChange={(e) => setExportEmail(e.target.value)}
                    disabled={loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="your@email.com"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Use the email address associated with your SuddyFlow account
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader className="animate-spin" size={20} />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Download size={20} />
                      Request Data Export
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-green-900 mb-1">
                    Export Request Received
                  </h3>
                  <p className="text-sm text-green-800">
                    We're preparing your data export. You'll receive an email at <strong>{exportEmail}</strong> within 
                    24 hours with a download link.
                  </p>
                  <p className="text-xs text-green-700 mt-2">
                    The download link will be valid for 7 days.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Reactivation Instructions */}
        <div className="border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <RefreshCw className="h-6 w-6 text-gray-600" />
            <h2 className="text-xl font-semibold text-gray-900">Want to Reactivate?</h2>
          </div>
          
          <p className="text-gray-600 mb-4">
            Changed your mind? You can reactivate your subscription anytime within the next 30 days 
            and pick up right where you left off.
          </p>

          <div className="space-y-3 mb-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-semibold text-sm">1</span>
              </div>
              <div className="flex-1 pt-1">
                <p className="text-sm text-gray-700">
                  <strong>Log back in</strong> to your SuddyFlow account using your email and password
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-semibold text-sm">2</span>
              </div>
              <div className="flex-1 pt-1">
                <p className="text-sm text-gray-700">
                  <strong>Choose a plan</strong> that fits your business needs
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-semibold text-sm">3</span>
              </div>
              <div className="flex-1 pt-1">
                <p className="text-sm text-gray-700">
                  <strong>Complete checkout</strong> and you're back in business!
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-3">
              <strong>Your data will be restored automatically</strong> when you reactivate. 
              No need to re-enter customers, jobs, or any other information.
            </p>
          </div>
        </div>

        {/* Contact Support */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <Mail className="h-5 w-5 text-gray-600" />
            <h3 className="font-semibold text-gray-900">Need Help?</h3>
          </div>
          <p className="text-sm text-gray-600">
            Questions about your cancellation or reactivation? Our support team is here to help.
          </p>
          <a
            href="mailto:support@suddyflow.com"
            className="inline-block mt-3 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            support@suddyflow.com →
          </a>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            to="/subscribe"
            className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors text-center"
          >
            Reactivate Now
          </Link>
          <Link
            to="/login"
            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors text-center"
          >
            Back to Login
          </Link>
        </div>

        {/* Footer Note */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            You're currently logged out. Your account credentials remain active for reactivation.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCancelled;